import React, { useState, useEffect, useRef } from 'react';
import { Button, Tooltip } from 'reactstrap';
import FeedbackMessage from './FeedbackMessage';
import { optimizeRoute, validateRouteData, reorderWaypoints } from '../../services/routeOptimizationService';

const loadingMessages = [
    'Finding the smartest route to save you money...',
    'Cutting miles means cutting costs — hang tight...',
    'Optimizing to keep more cash in your pocket...',
    'Making every mile count (and every dollar too)...',
    'Finding shortcuts to save time and fuel...',
    'Less driving = less spending. Working on it...',
    'Trimming the fat off your route...',
    'Helping your wallet breathe easier...',
    'Route smarter, not harder...',
    'Maximizing efficiency, minimizing expenses...',
    'Every optimized mile is money saved...',
    'Turning your route into a money-saver...',
    'Finding the path that costs you less...',
    'Squeezing every penny out of this route...',
    'Making your delivery budget go further...',
    'Fuel prices are high — let us help you save...',
    'Smart routing for smart savings...',
    'Your driver (and wallet) will thank you...',
    'Calculating the most cost-effective path...',
    'Working hard to save you money...',
    'Fewer miles, fewer dollars spent...',
    'Optimizing like your budget depends on it...',
    'Finding the route that makes financial sense...',
    'Time is money — we are saving you both...',
    'Making multi-drop deliveries more affordable...'
];

const OptimizeRouteButton = ({
    pickupInfo,
    waypointsArray,
    dropoffInfo,
    pickupItems,
    dropoffItems,
    onOptimized,
    setWaypointsArray
}) => {
    const [message, setMessage] = useState('');
    const [isClickDisabled, setIsClickDisabled] = useState(true);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [, setIsTyping] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const typingIntervalRef = useRef(null);

    const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

    // Validate inputs and enable/disable button
    useEffect(() => {
        const validation = validateRouteData(pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems);
        setIsClickDisabled(!validation.isValid);
    }, [pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems]);

    const typeMessage = fullMessage => {
        const words = fullMessage.split(' ');
        let currentIndex = 0;

        setIsTyping(true);
        setMessage('');

        typingIntervalRef.current = setInterval(() => {
            if (currentIndex < words.length) {
                setMessage(prev => prev + (currentIndex === 0 ? '' : ' ') + words[currentIndex]);
                currentIndex++;
            } else {
                clearInterval(typingIntervalRef.current);
                setIsTyping(false);
            }
        }, 300);
    };

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
            }
        };
    }, []);

    const handleOptimizeRoute = async () => {
        setIsClickDisabled(true);
        setIsLoading(true);

        // Show random loading message
        const randomMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
        typeMessage(randomMessage);

        try {
            const result = await optimizeRoute(pickupInfo, waypointsArray, dropoffInfo, pickupItems, dropoffItems);

            if (result.success && result.optimizedOrder) {
                // Reorder the waypoints array based on optimization result
                const reorderedWaypoints = reorderWaypoints(waypointsArray, result.optimizedOrder);

                // Update the waypoints through the setter
                if (setWaypointsArray) {
                    setWaypointsArray(reorderedWaypoints);
                }

                // Call the callback if provided
                if (onOptimized) {
                    onOptimized(reorderedWaypoints, result);
                }

                // Show success message with distance/duration if available
                let successMsg = 'Route optimized! Review the new order below.';
                if (result.estimatedDistance) {
                    successMsg = `Route optimized! Est. ${result.estimatedDistance.toFixed(1)} miles`;
                    if (result.estimatedDuration) {
                        const hours = Math.floor(result.estimatedDuration / 3600);
                        const minutes = Math.floor((result.estimatedDuration % 3600) / 60);
                        if (hours > 0) {
                            successMsg += `, ${hours}h ${minutes}m`;
                        } else {
                            successMsg += `, ${minutes} min`;
                        }
                    }
                }

                // Clear any ongoing typing animation before showing success
                if (typingIntervalRef.current) {
                    clearInterval(typingIntervalRef.current);
                    typingIntervalRef.current = null;
                }
                setIsTyping(false);
                setMessage(successMsg);
                setIsLoading(false);

                // Re-enable button after a short delay
                setTimeout(() => {
                    setIsClickDisabled(false);
                }, 2000);
            } else {
                throw new Error('Optimization failed - no result returned');
            }
        } catch (error) {
            // Clear any ongoing typing animation before showing error
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
                typingIntervalRef.current = null;
            }
            setIsTyping(false);
            setMessage(error.message || 'Route optimization failed. Please try again.');
            setIsLoading(false);
            setIsClickDisabled(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <div id="optimizeRouteWrapper" style={{ display: 'inline-block' }}>
                <Button
                    className="btn submit-btn"
                    color={isClickDisabled ? 'light' : 'info'}
                    onClick={handleOptimizeRoute}
                    disabled={isClickDisabled || isLoading}
                >
                    <span
                        style={{
                            marginRight: 8,
                            display: 'inline-block',
                            fontSize: '1.2em',
                            lineHeight: 1
                        }}
                        role="img"
                        aria-label="route"
                    >
                        🗺️
                    </span>
                    {isLoading ? 'OPTIMIZING...' : 'OPTIMIZE ROUTE'}
                </Button>
            </div>
            <Tooltip isOpen={tooltipOpen} toggle={toggleTooltip} target="optimizeRouteWrapper" placement="top">
                <div
                    style={{
                        background: '#fff',
                        color: '#222',
                        borderRadius: 8,
                        padding: '16px 18px',
                        fontSize: 13,
                        lineHeight: 1.6,
                        minWidth: 350,
                        width: 350,
                        maxWidth: 350,
                        border: '1.5px solid #b5d2ee',
                        textAlign: 'left',
                        boxShadow: 'none'
                    }}
                >
                    <strong>This is optional.</strong>
                    <br /><br />
                    <strong>What it does:</strong>
                    <br />
                    Reorders your middle stops to find the most efficient driving route, potentially reducing mileage and delivery costs.
                    <br /><br />
                    <strong>What stays the same:</strong>
                    <br />
                    Your first pickup and final dropoff locations remain fixed. Only the middle waypoints are reordered.
                    <br /><br />
                    <strong>Pickup/Dropoff order:</strong>
                    <br />
                    Items are always picked up before they are dropped off — the optimizer respects these constraints.
                </div>
            </Tooltip>
            <FeedbackMessage message={message} showSpinner={message.includes('...')} />
        </div>
    );
};

export default OptimizeRouteButton;
